import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NumberFormatBase } from 'react-number-format';
import { DraftFunction } from 'use-immer';

import { WizardValidationError } from '@/types/Wizard';

import Button from '@/components/elements/button';
import ErrorTag from '@/components/elements/error-tag';

import classes from './styles.module.scss';

interface PaymentFormProps {
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: Record<string, any>;
    submittingPayment: boolean;
    update: (key: string, event: React.BaseSyntheticEvent) => void;
    updateExpiryDate: (values: Record<string, string | number>) => void;
    updateForm?: (
        arg: Record<string, string> | DraftFunction<Record<string, string>>
    ) => void;
    moveIn?: boolean;
    validationErrors: WizardValidationError[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function CardExpiry(props: any) {
    const format = (val: string) => {
        if (val === '') return '';
        let month = val.substring(0, 2);
        const year = val.substring(2, 4);

        if (month.length === 1 && parseInt(month[0]) > 1) {
            month = `0${month[0]}`;
        } else if (month.length === 2) {
            // set the lower and upper boundary
            if (Number(month) === 0) {
                month = `01`;
            } else if (Number(month) > 12) {
                month = '12';
            }
        }

        return `${month}/${year}`;
    };

    return (
        <NumberFormatBase
            {...props}
            placeholder='Expiry (MM/YY)*'
            format={format}
            value={`${props.month}/${props.year}`}
        />
    );
}

export default function PaymentForm({
    onSubmit,
    form,
    submittingPayment,
    update,
    updateExpiryDate,
    moveIn,
    validationErrors,
}: PaymentFormProps) {
    const { t } = useTranslation();

    const formErrors = useMemo(() => {
        const inputNames: string[] = [
            'cardHolderName',
            'cardNumber',
            'cardExpiryMonth',
            'cardExpiryYear',
            'cardCsv',
        ];

        return validationErrors?.filter((error) => inputNames.includes(error.name));
    }, [validationErrors]);

    return (
        <>
            {moveIn && (
                <span className={`${classes.header_text}`}>
                    {t('wizard:paymentForm:title')}
                </span>
            )}

            {formErrors.map((error, index) => (
                <ErrorTag key={index} error={error} />
            ))}

            <form
                className={`${classes.payment_fields}`}
                onSubmit={onSubmit}
                noValidate={true}
            >
                <div className={`${classes.inputs}`}>
                    <div>
                        <input
                            type='text'
                            placeholder='Card Holder Name*'
                            required
                            name='cardHolderName'
                            value={form.cardHolderName}
                            disabled={submittingPayment}
                            onChange={(e) => update('cardHolderName', e)}
                        />
                    </div>

                    <div>
                        <input
                            type='text'
                            placeholder='Card Number*'
                            required
                            name='cardNumber'
                            value={form.cardNumber}
                            disabled={submittingPayment}
                            onChange={(e) => update('cardNumber', e)}
                        />
                    </div>

                    <div>
                        <CardExpiry
                            required
                            name='cardExpiry'
                            disabled={submittingPayment}
                            onValueChange={(values: Record<string, string | number>) => {
                                updateExpiryDate(values);
                            }}
                            month={form.cardExpiryMonth}
                            year={form.cardExpiryYear}
                        />
                    </div>

                    <div>
                        <input
                            type='text'
                            placeholder='CSV*'
                            name='cardCsv'
                            required
                            value={form.cardCsv}
                            disabled={submittingPayment}
                            onChange={(e) => update('cardCsv', e)}
                        />
                    </div>
                </div>

                {!moveIn && (
                    <Button
                        disabled={submittingPayment}
                        disableScroll
                        type={'primary'}
                        size={'medium'}
                    >
                        {t('wizard:next')}
                    </Button>
                )}
            </form>
        </>
    );
}
